import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Send, Users, UserSquare2, ListPlus, Sparkles, Bookmark, Trash2, MousePointerClick, Eye } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageTitle } from "./DashboardLayout";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const input = "w-full h-10 px-3 rounded-lg border border-input text-sm focus:outline-none focus:ring-2 focus:ring-electric";
const fmtDate = (d) => (d ? new Date(d).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");

export default function Broadcasts() {
  const qc = useQueryClient();
  const { data: templates } = useQuery({ queryKey: ["email-templates"], queryFn: () => api.get("/email-templates").then((r) => r.data.templates) });
  const { data: filters } = useQuery({ queryKey: ["broadcast-filters"], queryFn: () => api.get("/broadcasts/filters").then((r) => r.data) });
  const { data: history } = useQuery({ queryKey: ["broadcasts"], queryFn: () => api.get("/broadcasts").then((r) => r.data.broadcasts) });
  const { data: segments } = useQuery({ queryKey: ["segments"], queryFn: () => api.get("/segments").then((r) => r.data.segments) });

  const [form, setForm] = useState({ subject: "", title: "", body: "", ctaText: "", ctaUrl: "" });
  const [audience, setAudience] = useState({ type: "customers", status: "", service: "" });
  const [manual, setManual] = useState("");
  const [count, setCount] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [sending, setSending] = useState(false);
  const [segId, setSegId] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const manualList = () => manual.split(/[\s,;]+/).map((e) => e.trim()).filter((e) => e.includes("@"));

  const loadSegment = (id) => {
    setSegId(id);
    const s = (segments || []).find((x) => x.id === id);
    if (!s) return;
    setAudience({ type: s.audienceType, status: s.status || "", service: s.service || "" });
    if (s.audienceType === "manual") setManual((s.manualEmails || []).join("\n"));
    setCount(null);
  };
  const saveSegment = async () => {
    const name = window.prompt("Name this audience segment (e.g. 'Warm PC-repair leads')");
    if (!name?.trim()) return;
    try {
      await api.post("/segments", { name: name.trim(), audienceType: audience.type, status: audience.status || null, service: audience.service || null, manualEmails: audience.type === "manual" ? manualList() : [] });
      qc.invalidateQueries({ queryKey: ["segments"] });
      toast.success("Segment saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const deleteSegment = async () => {
    if (!segId) return;
    if (!window.confirm("Delete this saved segment?")) return;
    try { await api.delete(`/segments/${segId}`); setSegId(""); qc.invalidateQueries({ queryKey: ["segments"] }); toast.success("Segment deleted"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const prefill = (key) => {
    const t = (templates || []).find((x) => x.key === key);
    if (t) setForm({ subject: t.subject, title: t.title, body: t.body, ctaText: t.ctaText || "", ctaUrl: "" });
  };

  const preview = async () => {
    if (audience.type === "manual") { setCount(manualList().length); return; }
    try {
      const p = new URLSearchParams({ audienceType: audience.type, status: audience.status, service: audience.service }).toString();
      const r = await api.get(`/broadcasts/audience?${p}`);
      setCount(r.data.count);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const send = async () => {
    setSending(true);
    try {
      const payload = { ...form, audienceType: audience.type, status: audience.status || null, service: audience.service || null, manualEmails: audience.type === "manual" ? manualList() : [] };
      const r = await api.post("/broadcasts/send", payload);
      toast.success(`Broadcast sent to ${r.data.sent} of ${r.data.totalRecipients} recipient(s)${r.data.failed ? ` · ${r.data.failed} failed` : ""}`);
      qc.invalidateQueries({ queryKey: ["broadcasts"] });
      setConfirm(false); setCount(null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const canSend = form.subject.trim() && form.title.trim() && form.body.trim();

  return (
    <div>
      <PageTitle title="Email Broadcasts" subtitle="Send a one-off email to your customers or leads — turn your CRM into a marketing engine" />
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Composer */}
        <div className="rounded-xl bg-white border border-slate-200 p-5 space-y-4">
          <div>
            <label className="text-sm text-slate-600 flex items-center gap-1.5 mb-1"><Sparkles className="h-3.5 w-3.5 text-electric" /> Start from a template (optional)</label>
            <select className={input} defaultValue="" onChange={(e) => e.target.value && prefill(e.target.value)} data-testid="broadcast-template">
              <option value="">— Write from scratch —</option>
              {(templates || []).filter((t) => t.category === "customer").map((t) => <option key={t.key} value={t.key}>{t.name}</option>)}
            </select>
          </div>
          <input className={input} placeholder="Subject line" value={form.subject} onChange={(e) => set("subject", e.target.value)} data-testid="broadcast-subject" />
          <input className={input} placeholder="Heading (inside the email)" value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="broadcast-title" />
          <textarea className={`${input} h-auto py-2`} rows={6} placeholder="Body (HTML allowed — use {firstName} to personalize)" value={form.body} onChange={(e) => set("body", e.target.value)} data-testid="broadcast-body" />
          <div className="grid grid-cols-2 gap-3">
            <input className={input} placeholder="Button text (optional)" value={form.ctaText} onChange={(e) => set("ctaText", e.target.value)} data-testid="broadcast-cta-text" />
            <input className={input} placeholder="Button URL (optional)" value={form.ctaUrl} onChange={(e) => set("ctaUrl", e.target.value)} data-testid="broadcast-cta-url" />
          </div>
          <p className="text-[11px] text-slate-400">Personalization tokens: <span className="font-mono">{"{firstName}"}</span>, <span className="font-mono">{"{email}"}</span></p>
        </div>

        {/* Audience */}
        <div className="rounded-xl bg-white border border-slate-200 p-5 space-y-4">
          <h3 className="font-heading font-semibold text-navy">Audience</h3>
          {(segments || []).length > 0 && (
            <div className="flex items-center gap-2">
              <select className={`${input} flex-1`} value={segId} onChange={(e) => loadSegment(e.target.value)} data-testid="segment-select">
                <option value="">Load a saved segment…</option>
                {(segments || []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              {segId && <button onClick={deleteSegment} className="p-2 rounded hover:bg-red-50 text-red-500 shrink-0" title="Delete segment" data-testid="segment-delete"><Trash2 className="h-4 w-4" /></button>}
            </div>
          )}
          <div className="grid grid-cols-3 gap-2">
            {[["customers", "Customers", Users], ["leads", "Leads", UserSquare2], ["manual", "Manual list", ListPlus]].map(([val, label, Icon]) => (
              <button key={val} onClick={() => { setAudience((a) => ({ ...a, type: val })); setCount(null); }} data-testid={`audience-${val}`}
                className={`flex flex-col items-center gap-1.5 p-3 rounded-lg border text-xs transition-colors ${audience.type === val ? "border-electric bg-electric/5 text-electric" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}>
                <Icon className="h-5 w-5" /> {label}
              </button>
            ))}
          </div>

          {audience.type === "leads" && (
            <div className="grid grid-cols-2 gap-3">
              <label className="text-sm text-slate-600">Status
                <select className={input} value={audience.status} onChange={(e) => { setAudience((a) => ({ ...a, status: e.target.value })); setCount(null); }} data-testid="audience-status">
                  <option value="">Any status</option>{(filters?.statuses || []).map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
              <label className="text-sm text-slate-600">Service
                <select className={input} value={audience.service} onChange={(e) => { setAudience((a) => ({ ...a, service: e.target.value })); setCount(null); }} data-testid="audience-service">
                  <option value="">Any service</option>{(filters?.services || []).map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
            </div>
          )}
          {audience.type === "manual" && (
            <textarea className={`${input} h-auto py-2`} rows={5} placeholder="Paste emails (comma, space, or newline separated)" value={manual} onChange={(e) => { setManual(e.target.value); setCount(null); }} data-testid="audience-manual-input" />
          )}

          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={preview} className="gap-1.5" data-testid="preview-audience-btn"><Users className="h-4 w-4" /> Preview recipients</Button>
            <Button variant="outline" onClick={saveSegment} className="gap-1.5" data-testid="save-segment-btn"><Bookmark className="h-4 w-4" /> Save segment</Button>
            {count !== null && <span className="text-sm text-navy font-medium" data-testid="recipient-count">{count} recipient{count === 1 ? "" : "s"}</span>}
          </div>

          <Button disabled={!canSend} onClick={() => setConfirm(true)} className="w-full bg-electric hover:bg-tech gap-1.5" data-testid="send-broadcast-btn"><Send className="h-4 w-4" /> Send Broadcast</Button>
          {!canSend && <p className="text-xs text-slate-400">Add a subject, heading, and body to enable sending.</p>}
        </div>
      </div>

      {/* History */}
      <h3 className="font-heading font-semibold text-navy mt-8 mb-3">Sent Broadcasts</h3>
      <div className="rounded-xl bg-white border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase font-mono"><tr><th className="text-left p-3">Subject</th><th className="text-left p-3">Audience</th><th className="text-left p-3">Recipients</th><th className="text-left p-3">Sent</th><th className="text-left p-3">Opens</th><th className="text-left p-3">Clicks</th><th className="text-left p-3">When</th></tr></thead>
          <tbody>
            {(history || []).length === 0 ? <tr><td colSpan={7} className="p-8 text-center text-slate-400">No broadcasts sent yet.</td></tr> :
              (history || []).map((b) => (
                <tr key={b.id} className="border-t border-slate-100" data-testid={`broadcast-row-${b.id}`}>
                  <td className="p-3 font-medium text-navy">{b.subject}</td>
                  <td className="p-3 text-slate-600 capitalize">{b.audienceType}{b.status ? ` · ${b.status}` : ""}{b.service ? ` · ${b.service}` : ""}</td>
                  <td className="p-3 text-slate-600">{b.totalRecipients}</td>
                  <td className="p-3 text-green-600">{b.sent}{b.failed ? <span className="text-red-500"> / {b.failed} failed</span> : ""}</td>
                  <td className="p-3 text-slate-600"><span className="inline-flex items-center gap-1"><Eye className="h-3.5 w-3.5 text-slate-400" />{b.opens || 0}</span></td>
                  <td className="p-3 text-slate-600"><span className="inline-flex items-center gap-1"><MousePointerClick className="h-3.5 w-3.5 text-slate-400" />{b.clicks || 0}</span></td>
                  <td className="p-3 text-slate-500">{fmtDate(b.createdAt)}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <Dialog open={confirm} onOpenChange={setConfirm}>
        <DialogContent><DialogHeader><DialogTitle>Send this broadcast?</DialogTitle>
          <DialogDescription>This will email <b>{count !== null ? count : "the selected"}</b> recipient{count === 1 ? "" : "s"} with subject “{form.subject}”. This cannot be undone.</DialogDescription></DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(false)} data-testid="broadcast-cancel">Cancel</Button>
            <Button onClick={send} disabled={sending} className="bg-electric hover:bg-tech gap-1.5" data-testid="broadcast-confirm-send"><Send className="h-4 w-4" /> {sending ? "Sending…" : "Send now"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
